import React, { ElementType, forwardRef, ReactNode } from 'react';
import { Box, BoxProps, LinearProgress, LinearProgressProps } from '@mui/material';
import { LoadingText, LoadingTextProps } from './LoadingText';

export interface LoaderProps extends BoxProps<ElementType> {
  /**
   * Value of progress
   */
  value?: number;
  /**
   * Affects component size
   */
  size?: LinearProgressProps['size'] | number;
  /**
   * Additional label before LoaderText
   */
  label?: ReactNode;
  /**
   * Optional custom bar props.
   */
  barProps?: LinearProgressProps;
  /**
   * Properties applied to LoaderText
   */
  loadingTextProps?: LoadingTextProps;
}

/**
 * Wrapper of LinearProgress, automatically selecting the appropriate variant and appending LoaderText
 */
export const Loader: React.FC<LoaderProps> = forwardRef(
  ({ className, value, size, barProps, label, loadingTextProps, ...rest }, ref) => {
    const { sx: barSx, ...restBar } = barProps ?? {};

    return (
      <Box
        data-testid="Loader"
        className={['FlowLoader-root', className].filter(Boolean).join(' ')}
        ref={ref}
        {...rest}
      >
        {value !== undefined && (
          <LoadingText data-testid="Loader.percentage" value={value} label={label} {...loadingTextProps} />
        )}
        <LinearProgress
          value={value}
          sx={{
            ...(typeof size === 'number' && { height: `${size}px` }),
            ...barSx,
          }}
          {...(typeof size !== 'number' && { size: size })}
          {...(value !== undefined && {
            variant: 'determinate',
            ['aria-valuenow']: value,
            ['aria-valuemin']: 0,
            ['aria-valuemax']: 100,
          })}
          {...restBar}
        />
      </Box>
    );
  },
);
